<template>
  <div class="animated fadeIn">
    <b-row class="justify-content-center">
      <b-col md="12">
        <project-wizard-header-nav :path="path" :step="current_step" />
        <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
          <i class="fal fa-spinner fa-spin" /> Loading
        </div>
        <div v-else>
          <project-wizard-step1
            v-if="current_step == 1"
            :sampleTypes="sample_type_collection"
            :project="project"
          />
          <project-wizard-step2
            v-else-if="current_step == 2"
            :sampleType="currentSampleType"
            :testPackageGroups="currentTestPackageGroups"
            :serviceQuestions="currentServiceQuestions"
            :turnarounds="currentTurnarounds"
            :managedLists="managedLists"
          >
            <template #belowTitle>
              <project-wizard-tracker
                v-if="currentSampleType"
                :sampleType="currentSampleType"
                class="mb-4"
              />
            </template>
          </project-wizard-step2>
          <project-wizard-step3
            v-else-if="step == 3"
            :sampleType="currentSampleType"
            :managedLists="managedLists"
            :compiledProject="compiledProject"
            @saveProjectAndCheckout="saveProjectAndCheckout"
          />
          <project-wizard-footer-nav
            :path="path"
            :step="current_step"
            @resetForm="resetForm"
          />
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import config from "../config";
import ProjectWizardStep1 from "./ProjectWizardStep1";
import ProjectWizardStep2 from "./ProjectWizardStep2";
import ProjectWizardStep3 from "./ProjectWizardStep3";
import ProjectWizardHeaderNav from "../components/ProjectWizardHeaderNav";
import ProjectWizardFooterNav from "../components/ProjectWizardFooterNav";
import ProjectModel from "@/models/lab/ProjectModel";
import ProjectWizardTracker from "../components/ProjectWizardTracker";
import { mapActions, mapGetters } from "vuex";
import { getCollection, getModel } from "@/shared/fetch-utils";

export default {
  name: "ProjectWizardSteps",
  components: {
    ProjectWizardHeaderNav,
    ProjectWizardStep1,
    ProjectWizardStep2,
    ProjectWizardStep3,
    ProjectWizardFooterNav,
    ProjectWizardTracker
  },
  props: {
    path: {
      type: String,
      default: null
    },
    step: {
      type: Number,
      default: null
    },
    project_id: {
      type: String,
      default: null
    }
  },
  computed: {
    ...mapGetters(["projectForm"]),
    currentSampleType() {
      return this.sample_type_collection &&
        this.sample_type_collection.models &&
        this.sample_type_collection.models.length &&
        this.projectForm.step_one.sample_type_id
        ? this.sample_type_collection.models.find(
            i => i.id === this.projectForm.step_one.sample_type_id
          )
        : null;
    },
    currentTestPackageGroups() {
      return this.test_package_group_collection &&
        this.test_package_group_collection.models &&
        this.test_package_group_collection.models.length &&
        this.projectForm.step_one.sample_type_id
        ? this.test_package_group_collection.models.filter(
            i => i.sample_type_id === this.projectForm.step_one.sample_type_id
          )
        : [];
    },
    managedLists() {
      return this.managed_list_collection &&
        this.managed_list_collection.models &&
        this.managed_list_collection.models.length
        ? this.managed_list_collection.models
        : [];
    },
    currentServiceQuestions() {
      return this.currentSampleType &&
        this.currentSampleType.getServiceQuestions().models &&
        this.currentSampleType.getServiceQuestions().models.length
        ? this.currentSampleType.getServiceQuestions().models
        : [];
    },
    currentTurnarounds() {
      return this.turnaround_collection &&
        this.turnaround_collection.models
        ? this.turnaround_collection.models
        : [];
    }
  },
  data() {
    return {
      current_step: null,
      loading: true,
      laboratory_id: process.env.VUE_APP_DEFAULT_LABORATORY_ID || null,
      sample_type_collection: null,
      test_package_group_collection: null,
      managed_list_collection: null,
      turnaround_collection: null,
      admin: config.admin,
      compiledProject: null,
      project: null
    };
  },
  methods: {
    ...mapActions(["RESET_STORE"]),
    async init() {
      this.managed_list_collection = await getCollection({
        collection_type: "managed_list",
        message: this.$bvToast
      });
      this.sample_type_collection = await getCollection({
        collection_type: "sample_type",
        connections: [
          // "service_instructions",
          // "service_questions",
          // "turnarounds"
        ],
        limit: "all",
        message: this.$bvToast
      });
      this.test_package_group_collection = await getCollection({
        collection_type: "test_package_group",
        filters: [
          {
            logic: "filterEquals",
            field: "laboratory_id",
            value: this.laboratory_id
          }
        ],
        connections: [
          "laboratory",
          "sampleType",
          "testPackages.testPackageSamples.testPackageAnalytes.analyte",
          "testPackages.testPackageSamples.testPackage"
        ],
        sort: [
          {
            logic: "sortAddAsc",
            field: "position"
          },
          {
            logic: "sortAddAsc",
            field: "name"
          }
        ],
        limit: "all",
        message: this.$bvToast
      })

      if (this.project_id) {
        this.project = await getModel({
          model_type: "project",
          id: this.project_id,
        })
      }

      this.loading = false;
    },
    async fetchTurnarounds () {
      if (!this.currentSampleType) return

      this.turnaround_collection = await getCollection({
        collection_type: "turnaround",
        filters: [
          {
            logic: "filterEquals",
            field: "sample_type_id",
            value: this.currentSampleType.id
          },
          {
            logic: "filterEquals",
            field: "active",
            value: 1
          }
        ],
        limit: "all",
        message: this.$bvToast
      })
    },
    resetForm() {
      this.RESET_STORE();
      this.$router.push("1");
    },
    compileProject() {
      const project = {
        laboratory_id: this.laboratory_id,
        customer_id: this.projectForm.step_one.customer_id,
        customer_location_id: this.projectForm.step_one.location_id,
        customer_contact_id:
          (this.projectForm.step_one.contacts &&
            this.projectForm.step_one.contacts.find(() => true)) ||
          null,
        submissions: []
      };
      this.projectForm.step_two.forEach(formSubmission => {
        const submission = {
          ...formSubmission,
          samples: []
        };

        delete submission.id;

        if (formSubmission.samples && formSubmission.samples.length) {
          submission.samples = formSubmission.samples.map(
            (formSample, index) => {
              const sample = {
                ...formSample,
                number: +(index + 1)
              };

              delete sample.test_package_sample_id;

              return sample;
            }
          );
        }

        if (formSubmission.zones && formSubmission.zones.length) {
          submission.samples = formSubmission.zones.reduce((acc, cur) => {
            const samples = cur.samples ? cur.samples.map((sample, index) => {
              return {
                number: acc.length + index + 1,
                identifier: sample.identifier,
                add_ons: sample.add_ons,
                ...sample.sample_soil,
                zone: cur.identifier
              };
            }) : [];

            return [
              ...acc,
              ...samples
            ];
          }, []);
        }

        if (formSubmission.bore_holes && formSubmission.bore_holes.length) {
          submission.samples = formSubmission.bore_holes.reduce((acc, cur) => {
            const samples = cur.samples ? cur.samples.map((sample, index) => {
              return {
                number: acc.length + index + 1,
                identifier: sample.identifier,
                add_ons: sample.add_ons || [],
                bore_hole: cur.identifier,
                depth_start: sample.depth_start,
                depth_end: sample.depth_end,
                texture: sample.field_texture,
                horizon: sample.soil_horizon
              };
            }) : [];

            return [
              ...acc,
              ...samples
            ];
          }, []);
        }

        project.submissions.push(submission);
      });

      this.compiledProject = { ...project };

      return project;
    },
    saveProjectAndCheckout() {
      const project = new ProjectModel();
      
      project.save({ ...this.compiledProject }).then(
        ({ response }) => {
          this.RESET_STORE();
          this.$router.push(`/projects/${response.data.id}`);
        },
        err => {
          this.$bvToast.toast(`${err}`, {
            title: "Project Save Failed",
            autoHideDelay: 5000,
          });
        }
      );
    }
  },
  watch: {
    step: {
      handler(step) {
        this.current_step = step;
      },
      immediate: true
    },
    projectForm: {
      handler() {
        this.compileProject();
      },
      immediate: true,
      deep: true
    },
    currentSampleType: {
      handler() {
        this.fetchTurnarounds()
      },
      immediate: true
    }
  },
  mounted() {
    this.loading = true;

    this.init();
  }
};
</script>
